import { useQuery } from '@tanstack/react-query';

import { useTRPC } from '#src/trpc.js';
import { isOrganizationId } from '../cadstrom-uid-utils.js';

/** The organization fields needed to label an organization and build routes scoped to it. */
export interface CommandBarOrganization {
	id: string;
	organizationName: string;
	displayName: string | null;
}

/**
 * Resolves an organization by id.
 *
 * Owners that are not organizations — a personal project's user id, or no owner at
 * all — resolve to `undefined` without a lookup, since only organizations have a page.
 *
 * @param ownerId - The owner to resolve, or `undefined` to skip the lookup.
 * @returns The organization, or `undefined` while it is loading, when the owner is
 *          not an organization, or when it is unavailable.
 */
export function useCommandBarOrganization(ownerId: string | undefined): CommandBarOrganization | undefined {
	const trpc = useTRPC();
	const isOrganizationOwner = ownerId !== undefined && isOrganizationId(ownerId);
	const { data } = useQuery(
		trpc.organization.get.queryOptions({ id: ownerId ?? '' }, { enabled: isOrganizationOwner, throwOnError: false }),
	);

	return data?.organization;
}

/**
 * Picks the friendliest name available for an organization.
 *
 * @param organization - The organization to name.
 * @returns The display name when set, otherwise its `organizationName`.
 */
export function organizationLabel(organization: CommandBarOrganization): string {
	return organization.displayName ?? organization.organizationName;
}
